import React, { memo, useCallback } from 'react';
import { isEmpty } from 'ramda';
import arePropsEqual from 'react-fast-compare';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Heading from '../Heading/Heading';
import GameCard from '../GameCard/GameCard';
import CardsGrid from '../../../components/CardsGrid/CardsGrid';
import { createMockEntitiesArray } from '../../../utils';
import { ANALYTICS_SCHEMA_URLS } from '../../../constants/analytics';
import styles from './GameCardsWidget.module.css';

const GameCardsWidget = ({
  title,
  link,
  items,
  lines,
  limit,
  isLoading,
  className,
  additionalLayerData,
  eventAction,
  eventActionPlay,
  eventActionPlayDemo,
  eventActionFavorite,
  sectionIndex,
  analyticsData,
  gameBg,
  style,
  ...props
}) => {
  const collection = isLoading ? createMockEntitiesArray(limit) : items;

  const renderCard = useCallback(
    (item, index) => {
      return (
        <GameCard
          key={item.id}
          sectionIndex={sectionIndex}
          index={index}
          eventActionPlay={eventActionPlay}
          eventActionPlayDemo={eventActionPlayDemo}
          additionalLayerData={additionalLayerData}
          eventActionFavorite={eventActionFavorite}
          game={item}
          isLoading={isLoading}
          analyticsData={{ ...analyticsData, section_index: sectionIndex }}
        />
      );
    },
    [
      additionalLayerData,
      eventActionPlay,
      eventActionPlayDemo,
      eventActionFavorite,
      isLoading,
      sectionIndex,
      analyticsData,
    ]
  );

  return isEmpty(collection) ? null : (
    <div
      className={classNames(
        styles.component_ig,
        gameBg && styles.component_ig__with_bg,
        className
      )}
      style={{
        '--game-widget-bg': gameBg && `url(${gameBg})`,
        ...style,
      }}
      {...props}
    >
      <Heading
        link={link}
        isLoading={isLoading}
        additionalLayerData={additionalLayerData}
        eventAction={eventAction}
        analyticsData={{
          ...analyticsData,
          ...additionalLayerData,
          section_index: sectionIndex,
          schema: ANALYTICS_SCHEMA_URLS.MORE_GAME_CLICK,
        }}
        className={styles.heading}
      >
        {title}
      </Heading>

      <CardsGrid
        items={collection}
        render={(game, gameIndex) => renderCard(game, gameIndex)}
        className={classNames(styles.grid_ig)}
        itemClassName={styles.item_ig}
      />
    </div>
  );
};

GameCardsWidget.propTypes = {
  link: PropTypes.string,
  items: PropTypes.array,
  lines: PropTypes.number,
  isLoading: PropTypes.bool,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  additionalLayerData: PropTypes.shape({}),
  eventAction: PropTypes.string,
  eventActionPlay: PropTypes.string,
  eventActionPlayDemo: PropTypes.string,
  sectionIndex: PropTypes.number,
  analyticsData: PropTypes.shape({
    screen_name: PropTypes.string,
    screen_area: PropTypes.string,
    screen_area_detail: PropTypes.string,
    event_detail: PropTypes.string,
  }),
  gameBg: PropTypes.string,
};

GameCardsWidget.defaultProps = {
  link: '',
  title: '',
  items: [],
  lines: 1,
  isLoading: false,
  analyticsData: {},
};

export default memo(GameCardsWidget, arePropsEqual);
